import React, { useEffect, useRef, useState } from "react";
import { Printer, FileText, RotateCcw, CloudUpload, Trash2, Mail, Loader2 } from "lucide-react";
import { Disclaimer } from "@/components/Badges";
import { MicButton } from "@/components/MicButton";
import { SensitiveInfoWarning } from "@/components/SensitiveInfoWarning";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY = {
  full_name: "",
  address: "",
  phone: "",
  email: "",
  case_number: "",
  county: "",
  household_size: "",
  monthly_income: "",
  income_sources: "",
  benefits: {
    snap: false, ssi: false, tanf: false, medicaid: false, unemployment: false, other: false,
  },
  benefits_other: "",
  monthly_rent: "",
  monthly_utilities: "",
  monthly_food: "",
  monthly_medical: "",
  monthly_transport: "",
  monthly_childcare: "",
  monthly_other: "",
  reason: "",
  date_signed: "",
};

const STORAGE_KEY = "pod_waiver_draft_v1";

function load() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null") || EMPTY; } catch { return EMPTY; }
}
function save(v) { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); }

export default function Waiver() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const signedIn = user && typeof user === "object";
  const [f, setF] = useState(load);
  const saveTimer = useRef(null);

  // Cloud pull (signed-in only). Prefer non-empty cloud, else push local up.
  useEffect(() => {
    if (!signedIn) return;
    (async () => {
      try {
        const { data } = await api.get("/waiver/state");
        const cloud = data?.data || {};
        const cloudHasData = Object.values(cloud).some((v) =>
          (typeof v === "string" && v.trim()) ||
          (typeof v === "object" && v && Object.values(v).some(Boolean))
        );
        if (cloudHasData) {
          const merged = { ...EMPTY, ...cloud, benefits: { ...EMPTY.benefits, ...(cloud.benefits || {}) } };
          setF(merged);
          save(merged);
        } else {
          await api.put("/waiver/state", { data: f });
        }
      } catch (err) {
        console.warn("waiver state pull failed", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  const cloudSave = (next) => {
    if (!signedIn) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/waiver/state", { data: next })
        .catch((err) => console.warn("waiver cloud sync failed", err));
    }, 500);
  };

  const upd = (k, v) => {
    const next = { ...f, [k]: v };
    setF(next); save(next); cloudSave(next);
  };
  const updBenefit = (k, v) => {
    const next = { ...f, benefits: { ...f.benefits, [k]: v } };
    setF(next); save(next); cloudSave(next);
  };
  const reset = () => {
    setF(EMPTY); save(EMPTY); cloudSave(EMPTY);
  };
  const clearAll = async () => {
    if (!window.confirm("Clear this fee-waiver draft from this device and your account?")) return;
    setF(EMPTY);
    save(EMPTY);
    localStorage.removeItem(STORAGE_KEY);
    if (signedIn) {
      try { await api.delete("/waiver/state"); toast.success("Draft cleared"); } catch (err) {
        console.warn("waiver clear failed", err);
        toast.error("Could not clear from your account");
      }
    } else {
      toast.success("Draft cleared from this device");
    }
  };

  const totalExp = ["monthly_rent","monthly_utilities","monthly_food","monthly_medical","monthly_transport","monthly_childcare","monthly_other"]
    .reduce((s,k) => s + (parseFloat(f[k]) || 0), 0);

  const [emailing, setEmailing] = React.useState(false);
  const emailDraft = async () => {
    setEmailing(true);
    try {
      const res = await api.post("/waiver/email", { draft: f, lang });
      toast.success(`${t("waiver.email_sent")} ${res.data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally { setEmailing(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 print:max-w-full">
      <div className="print:hidden">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <FileText className="w-6 h-6 text-[#C85A32]" /> {t("waiver.title")}
        </h1>
        <p className="text-[#4A525A] mt-1">
          {t("waiver.subtitle")}
        </p>
        <div className="text-xs mt-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
          {t("waiver.disclaimer")}
        </div>
        <div className="text-xs mt-2 text-[#4A525A] bg-[#F1F5F9] border border-[#E4DDD3] rounded-lg p-2 flex items-start gap-2" data-testid="waiver-storage-notice">
          {signedIn ? (
            <>
              <CloudUpload className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-700" />
              <span>{t("waiver.storage_signed_in")}</span>
            </>
          ) : (
            <span>{t("waiver.storage_anonymous")}</span>
          )}
        </div>
        <div className="mt-2"><SensitiveInfoWarning testid="waiver-sensitive-warning" /></div>
      </div>

      <div className="flex flex-wrap gap-2 print:hidden">
        <button
          onClick={() => window.print()}
          data-testid="waiver-print"
          className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] text-white font-bold py-2.5 px-4 rounded-xl inline-flex items-center gap-2"
        >
          <Printer className="w-4 h-4" /> {t("waiver.print")}
        </button>
        {signedIn ? (
          <button
            onClick={emailDraft}
            disabled={emailing}
            data-testid="waiver-email"
            className="pod-focus bg-[#2C4A3E] hover:bg-[#1F382E] disabled:opacity-70 text-white font-bold py-2.5 px-4 rounded-xl inline-flex items-center gap-2"
          >
            {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {t("waiver.email")}
          </button>
        ) : (
          <span className="inline-flex items-center gap-2 text-sm text-[#707880] py-2.5 px-2" data-testid="waiver-email-signin-hint">
            <Mail className="w-4 h-4" /> {t("waiver.email_signin")}
          </span>
        )}
        <button
          onClick={reset}
          data-testid="waiver-reset"
          className="pod-focus border-2 border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024] font-bold py-2.5 px-4 rounded-xl inline-flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" /> {t("waiver.reset")}
        </button>
        <button
          onClick={clearAll}
          data-testid="waiver-clear"
          className="pod-focus border-2 border-red-200 hover:border-red-500 text-red-700 font-bold py-2.5 px-4 rounded-xl inline-flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" /> {t("waiver.clear")}
        </button>
      </div>

      <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5 sm:p-8 print:border-0 print:rounded-none print:p-0">
        <h2 className="text-xl font-bold">{t("waiver.section_case")}</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Text label="Full legal name" val={f.full_name} onChange={(v) => upd("full_name", v)} testid="waiver-name" />
          <Text label="Case number (if you have one)" val={f.case_number} onChange={(v) => upd("case_number", v)} testid="waiver-case" />
          <Text label="Address" val={f.address} onChange={(v) => upd("address", v)} testid="waiver-address" />
          <Text label="County" val={f.county} onChange={(v) => upd("county", v)} testid="waiver-county" />
          <Text label="Phone" val={f.phone} onChange={(v) => upd("phone", v)} testid="waiver-phone" />
          <Text label="Email" val={f.email} onChange={(v) => upd("email", v)} testid="waiver-field-email" />
        </div>

        <h3 className="mt-8 text-lg font-bold">{t("waiver.section_household")}</h3>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Text label="Household size (including you)" val={f.household_size} onChange={(v) => upd("household_size", v)} testid="waiver-household" />
          <Text label="Total monthly income ($)" val={f.monthly_income} onChange={(v) => upd("monthly_income", v)} testid="waiver-income" />
          <Text label="Sources of income (job, benefits, family help)" val={f.income_sources} onChange={(v) => upd("income_sources", v)} testid="waiver-sources" full />
        </div>

        <h3 className="mt-8 text-lg font-bold">{t("waiver.section_benefits")}</h3>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[["snap","SNAP / Food Stamps"],["ssi","SSI"],["tanf","TANF"],["medicaid","Medicaid"],["unemployment","Unemployment"],["other","Other"]].map(([k,label]) => (
            <label key={k} className="flex items-center gap-2 bg-[#FAF5EC] border border-[#E4DDD3] rounded-xl px-3 py-2">
              <input
                type="checkbox"
                data-testid={`waiver-benefit-${k}`}
                checked={f.benefits[k]}
                onChange={(e) => updBenefit(k, e.target.checked)}
                className="w-4 h-4 accent-[#C85A32]"
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        {f.benefits.other && (
          <Text label="Other benefit — describe" val={f.benefits_other} onChange={(v) => upd("benefits_other", v)} testid="waiver-benefit-other-desc" full />
        )}

        <h3 className="mt-8 text-lg font-bold">{t("waiver.section_expenses")}</h3>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Text label="Rent / mortgage" val={f.monthly_rent} onChange={(v) => upd("monthly_rent", v)} testid="waiver-rent" />
          <Text label="Utilities" val={f.monthly_utilities} onChange={(v) => upd("monthly_utilities", v)} testid="waiver-utilities" />
          <Text label="Food" val={f.monthly_food} onChange={(v) => upd("monthly_food", v)} testid="waiver-food" />
          <Text label="Medical" val={f.monthly_medical} onChange={(v) => upd("monthly_medical", v)} testid="waiver-medical" />
          <Text label="Transportation" val={f.monthly_transport} onChange={(v) => upd("monthly_transport", v)} testid="waiver-transport" />
          <Text label="Childcare" val={f.monthly_childcare} onChange={(v) => upd("monthly_childcare", v)} testid="waiver-childcare" />
          <Text label="Other essentials" val={f.monthly_other} onChange={(v) => upd("monthly_other", v)} testid="waiver-other" />
        </div>
        <div className="mt-3 text-right text-sm text-[#4A525A]">
          {t("waiver.total_exp")} <span className="font-bold mono text-[#1C2024]" data-testid="waiver-total-exp">${totalExp.toFixed(2)}</span>
        </div>

        <h3 className="mt-8 text-lg font-bold">{t("waiver.section_reason")}</h3>
        <div className="flex items-start gap-2 mt-2">
          <textarea
            rows={4}
            data-testid="waiver-reason"
            value={f.reason}
            onChange={(e) => upd("reason", e.target.value)}
            className="flex-1 rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-3 bg-white"
            placeholder={t("waiver.reason_placeholder")}
          />
          <MicButton
            testid="waiver-mic-reason"
            size="sm"
            label="Speak your reason"
            onTranscript={(t) => upd("reason", (f.reason ? f.reason + " " : "") + t)}
          />
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">Signature</div>
            <div className="mt-1 h-12 border-b-2 border-[#1C2024]/60" />
          </div>
          <Text label="Date signed" val={f.date_signed} onChange={(v) => upd("date_signed", v)} testid="waiver-date" />
        </div>

        <div className="mt-8 text-xs text-[#4A525A] print:mt-4">
          I certify that the information above is true and correct to the best of my knowledge.
        </div>
      </div>

      <Disclaimer />
    </div>
  );
}

function Text({ label, val, onChange, testid, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{label}</span>
      <input
        type="text"
        data-testid={testid}
        value={val || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-3 bg-white"
      />
    </label>
  );
}
